/**
 * LP-24 My Clients: the parts every section on the page shares, so a
 * group reads the same wherever it sits. Each follows
 * docs/reference/design-language.md.
 *
 *   - SECTION_CARD: the card recipe (§4), for a section with something in
 *     it. An empty section is one quiet line instead (QuietSection).
 *   - GroupHead: one head for a group of rows inside a card (a task
 *     bucket). A neutral band (sand, the neutral register), the label in
 *     the title-sm role, the count as plain text at the right. Overdue
 *     alone asks something of the reader, so it alone takes the
 *     attention register (pepper-soft). A group that opens and closes
 *     passes `onToggle`: the whole band is then the button, with a
 *     chevron after the count.
 *   - ROW_LIST: rows inside a card divide with one hairline (§6).
 *   - ROW_CAP and ShowAllToggle: a section or group shows its first five
 *     rows, then "Show all N". The count is on the head, so nothing is
 *     hidden without saying so.
 */
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/primitives/Button';

export const SECTION_CARD = 'rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh1';

/** A row list inside a card: one hairline between rows, none around. */
export const ROW_LIST = 'divide-y divide-sage-800/[8%]';

/** Rows a section or group shows before "Show all". */
export const ROW_CAP = 5;

/** `attention` is Overdue's register; every other group is neutral. */
export type GroupTone = 'neutral' | 'attention';

const TONE: Record<GroupTone, string> = {
  neutral: 'bg-sand',
  attention: 'bg-pepper-soft',
};

export function GroupHead({
  label,
  count,
  tone = 'neutral',
  expanded,
  onToggle,
  controls,
  testId,
}: {
  label: string;
  count: number;
  tone?: GroupTone;
  expanded?: boolean;
  onToggle?: () => void;
  /** The id of the rows this head opens and closes, when they stay mounted. */
  controls?: string;
  testId?: string;
}): ReactNode {
  const cls = `flex w-full items-center gap-2 rounded-base ${TONE[tone]} px-2.5 py-1.5 text-left text-title-sm font-semibold text-sage-800`;
  const body = (
    <>
      <span className="min-w-0 flex-1">{label}</span>
      <span data-testid={testId ? `${testId}-count` : undefined} className="flex-shrink-0 font-medium tabular-nums text-ink-soft">
        {count}
      </span>
    </>
  );
  if (!onToggle) {
    return <div data-testid={testId} className={cls}>{body}</div>;
  }
  return (
    <button
      type="button"
      data-testid={testId}
      aria-expanded={expanded === true}
      aria-controls={controls}
      onClick={onToggle}
      className={`${cls} hover:bg-sage-800/[6%] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400`}
    >
      {body}
      <ChevronRight
        size={14}
        aria-hidden="true"
        className={'flex-shrink-0 text-ink-soft transition-transform ' + (expanded ? 'rotate-90' : '')}
      />
    </button>
  );
}

/** An empty section: its title and its own empty line, no card (§4,
 *  emptiness is quiet). It keeps the card's inset (the border's width,
 *  unseen, and p-4), so its title lines up with a card's beside it. */
export function QuietSection({
  headingId,
  title,
  children,
}: {
  headingId: string;
  title: string;
  /** The section's empty copy (and an action, when it has one). */
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border border-transparent p-4">
      <h2 id={headingId} className="font-display text-lg leading-7 text-sage-800">
        {title}
      </h2>
      {children}
    </div>
  );
}

/** "Show all N" under a capped list; "Show fewer" once open. Renders
 *  nothing when the list fits under the cap. */
export function ShowAllToggle({
  total,
  open,
  onToggle,
  testId,
}: {
  total: number;
  open: boolean;
  onToggle: () => void;
  testId?: string;
}): ReactNode {
  if (total <= ROW_CAP) return null;
  return (
    <Button variant="ghost" size="sm" className="self-start" data-testid={testId} aria-expanded={open} onClick={onToggle}>
      {open ? 'Show fewer' : `Show all ${total}`}
    </Button>
  );
}
